<template>
  <div>
    <page-header
      has-return
      :page="pageConfig"
      :actions="pageActionsConfig"
      left-side-class="col-12 col-lg-8"
      right-side-class="col-12 col-lg-4 d-flex align-items-center justify-content-end"
      @onReturn="() => $router.back()"
    />
    <filter-manager
      ref="filterRef"
      v-model="filterModel.filter"
      open-advanced
      items-container-class="flex-wrap"
      actions-container-class="col-sm-3 ml-0 mr-0 text-right"
      :filters="filterConfig"
      @search="handleSearch"
    />
    <custom-separator class="my-4" text="Resultados" />
    <div v-if="results.length > 0">
      <div class="mb-3" v-for="(item, index) in results">
        <search-visit-result
          :item="item"
          :index="index"
          @toggle="handleToggle"
          @enter="handleEnter"
        />
      </div>
      <pagination
        v-if="pagination.totalItemCount > pagination.pageSize"
        :data="pagination"
        :page-size="pagination.pageSize"
        @pagination-go-page="handlePageChange"
      />
    </div>
    <div v-else>
      <custom-result-not-found />
    </div>
  </div>
</template>

<script>
// third party libs
import moment from 'moment';
import 'moment/locale/es-do';

// libs
import PageHeader from 'PageHeader';
import FilterManager from 'FilterManager';
import CustomSeparator from 'custom-separator';
import Pagination from 'pagination';
import CustomResultNotFound from 'custom-result-not-found';

// child components
import { SearchVisitResult } from './components';

// services
import {
  getInitFilters as _getInitFilters,
  getMonths as _getMonths,
  getProviders as _getProviders,
  getSpecialists as _getSpecialists,
  getCenters as _getCenters,
  getServices as _getServices,
  getLocations as _getLocations,
  searchVisits as _searchVisits,
} from '@/services/pei/visit-registry.js';
import * as locationApi from '@/views/pei/location-alternatives/_api';

// mixins
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';
import storeMixin from '@quisit/mipe-utils/mixins/storeGenerator';

// constants
const MODULE_NAME = 'VisitRegistryModule';
const VISIT_REGISTRATION_ROUTE_NAME = 'visit-registration';

export default {
  components: {
    PageHeader,
    FilterManager,
    CustomSeparator,
    Pagination,
    CustomResultNotFound,
    SearchVisitResult,
  },
  mixins: [alertMixin, storeMixin],
  data: () => ({
    moduleName: MODULE_NAME,
    filterModel: {
      filter: {
        academicYear: undefined,
        month: undefined,
        provider: undefined,
        specialist: undefined,
        center: undefined,
        municipality: undefined,
        service: undefined,
        locationType: undefined,
        location: undefined,
        age: undefined,
        action: undefined,
        sie: undefined,
      },
      collections: {
        academicYears: [],
        months: [],
        providers: [],
        specialists: [],
        centers: [],
        municipalities: [],
        services: [],
        locationTypes: [],
        locations: [],
        ages: [],
        actions: [],
      },
    },
    results: [],
    pagination: {
      count: 0,
      currentPage: 1,
      pageCount: 1,
      totalItemCount: 0,
      pageSize: 15,
    },
    isInitialized: false,
  }),
  computed: {
    pageConfig() {
      return {
        icon: 'fas fa-user-tie',
        title: 'Registro de Visitas',
        routes: ['Inicio', 'PEI', 'Registro de Visitas'],
      };
    },
    pageActionsConfig() {
      return [
        {
          name: 'Certificaciones de Visita',
          icon: 'fa fa-clipboard',
          variant: 'success',
          text: 'Certificaciones de Visita',
          visible: true,
          click: () => {},
        },
      ];
    },
    filterConfig() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          options: this.filterModel.collections.academicYears,
          value: this.filterModel.filter.academicYear,
          placeholder: 'Seleccione el año',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: false,
          class: 'col-sm-12 col-md-4',
          classContainer: 'w-100',
          validations: 'required',
          error: 'El año académico es requerido.',
          onChanged: async (value) => {
            this.clearModels([
              'month',
              'provider',
              'specialist',
              'center',
              'municipality',
              'service',
              'locationType',
              'location',
              'specialist',
            ]);
            if (value) await this.getMonths();
          },
        },
        {
          name: 'month',
          component: 'CustomDropDown',
          options: this.filterModel.collections.months,
          value: this.filterModel.filter.month,
          placeholder: 'Seleccione el Mes',
          fieldName: 'monthDesc',
          fieldKey: 'monthNumber',
          clearable: true,
          class: 'col-sm-12 col-md-4',
          classContainer: 'w-100',
          validations: 'required',
          error: 'El mes es requerido.',
          onChanged: async (value) => {
            this.clearModels([
              'provider',
              'specialist',
              'center',
              'municipality',
              'service',
              'locationType',
              'location',
              'specialist',
            ]);
            if (value) await this.getProviders();
          },
        },
        {
          name: 'provider',
          component: 'CustomDropDown',
          options: this.filterModel.collections.providers,
          value: this.filterModel.filter.provider,
          placeholder: 'Seleccione el Proveedor',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: false,
          class: 'col-sm-12 col-md-4',
          classContainer: 'w-100',
          clearable: true,
          validations: 'required',
          error: 'El proveedor es requerido.',
          disabled: this.filterModel.collections.providers.length == 1,
          onChanged: async (value) => {
            this.clearModels([
              'specialist',
              'center',
              'municipality',
              'service',
              'locationType',
              'location',
              'specialist',
            ]);
            if (value) {
              await this.getCenters();
            }
          },
        },
        {
          name: 'center',
          component: 'CustomDropDown',
          options: this.filterModel.collections.centers,
          label: 'Centro:',
          value: this.filterModel.filter.center,
          placeholder: 'Seleccione',
          class: 'col-12 col-md-3',
          fieldName: 'name',
          fieldKey: 'id',
          isAdvanced: true,
          clearable: true,
          disabled: this.filterModel.collections.centers.length == 1,
          validations: 'required',
          error: 'El centro es requerido.',
          onChanged: () => {
            this.clearModels([
              'municipality',
              'service',
              'locationType',
              'location',
              'specialist',
            ]);
            this.getMunicipalities();
          },
        },
        {
          name: 'municipality',
          component: 'CustomDropDown',
          options: this.filterModel.collections.municipalities,
          label: 'Municipio:',
          value: this.filterModel.filter.municipality,
          placeholder: 'Seleccione',
          fieldKey: 'id',
          fieldName: 'name',
          class: 'col-12 col-md-3',
          validations: 'required',
          error: 'El servicio es requerido.',
          clearable: true,
          isAdvanced: true,
          disabled: this.filterModel.collections.municipalities.length == 1,
          onChanged: () => {
            this.clearModels([
              'service',
              'locationType',
              'location',
              'specialist',
            ]);
            this.getLocationTypes();
            this.getServices();
          },
        },
        {
          name: 'locationType',
          component: 'CustomDropDown',
          options: this.filterModel.collections.locationTypes,
          label: 'Tipo de Lugar:',
          value: this.filterModel.filter.locationType,
          placeholder: 'Seleccione',
          class: 'col-12 col-md-3',
          validations: 'required',
          error: 'El servicio es requerido.',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          isAdvanced: true,
          disabled:
            this.filterModel.filter.locationType &&
            this.filterModel.collections.locationTypes.length == 1,
          onChanged: () => {
            this.clearModels(['location', 'specialist']);
            this.getLocations();
          },
        },
        {
          name: 'location',
          component: 'CustomDropDown',
          options: this.filterModel.collections.locations,
          label: 'Escuela:',
          value: this.filterModel.filter.location,
          placeholder: 'Seleccione',
          class: 'col-12 col-md-3',
          validations: 'required',
          error: 'El servicio es requerido.',
          fieldName: 'name',
          fieldKey: 'id',
          mapParentValue: this.filterModel.filter.location == null,
          isAdvanced: true,
          clearable: true,
          disabled: this.filterModel.collections.locations.length == 1,
          onChanged: () => {
            this.getSpecialists();
          },
        },
        {
          name: 'specialist',
          component: 'CustomDropDown',
          options: this.filterModel.collections.specialists,
          label: 'Especialista:',
          value: this.filterModel.filter.specialist,
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: false,
          class: 'col-sm-12 col-md-3',
          classContainer: 'w-100',
          clearable: true,
          isAdvanced: true,
          onChanged: async (value) => {},
        },
        {
          name: 'service',
          component: 'CustomDropDown',
          options: this.filterModel.collections.services,
          label: 'Servicio:',
          value: this.filterModel.filter.service,
          placeholder: 'Seleccione',
          fieldKey: 'id',
          fieldName: 'name',
          class: 'col-12 col-md-3',
          disabled: this.filterModel.collections.services.length == 1,
          clearable: true,
          isAdvanced: true,
        },
        {
          name: 'age',
          component: 'CustomDropDown',
          options: this.filterModel.collections.ages,
          label: 'Edad:',
          value: this.filterModel.filter.age,
          placeholder: 'Seleccione',
          class: 'col-12 col-md-3',
          fieldName: 'name',
          fieldKey: 'id',
          mapParentValue: this.filterModel.filter.age == null,
          isAdvanced: true,
          clearable: true,
          disabled: this.filterModel.collections.ages.length == 1,
        },
        {
          name: 'action',
          component: 'CustomDropDown',
          options: this.filterModel.collections.actions,
          label: 'Acción:',
          value: this.filterModel.filter.action,
          placeholder: 'Seleccione',
          class: 'col-12 col-md-3',
          fieldName: 'name',
          fieldKey: 'id',
          mapParentValue: this.filterModel.filter.action == null,
          isAdvanced: true,
          clearable: true,
          disabled: this.filterModel.collections.actions.length == 1,
        },
        {
          name: 'sie',
          component: 'CustomInput',
          label: 'SIE:',
          type: 'text',
          placeholder: 'Escriba número de SIE',
          icon: 'fa fa-search orange-icon',
          leftIcon: true,
          class: 'col-12 col-md-3',
          isAdvanced: true,
        },
      ];
    },
  },
  async mounted() {
    await this.getInitFilters();
  },
  destroyed() {
    this.persistData();
  },
  beforeRouteEnter(_, from, next) {
    next((vm) => {
      if (from.name == VISIT_REGISTRATION_ROUTE_NAME) {
        vm.recoveryData();
      } else {
        vm.shouldRecovery = false;
      }
    });
  },
  methods: {
    async getInitFilters() {
      const response = await _getInitFilters().catch((error) => {
        this.ShowToast('Error', error.message, 'error');
      });
      if (response?.data) {
        const data = response.data;
        this.filterModel = {
          filter: this.mergeFilter(data.filter, this.filterModel.filter),
          collections: this.mergeArrayCollections(
            data.collections,
            this.filterModel.collections
          ),
        };
        if (!this.filterModel.filter.academicYear) {
          this.filterModel.filter.academicYear =
            data.collections?.academicYears?.[0];
        }
        this.isInitialized = true;
      }
    },
    async getMonths() {
      const { academicYear } = this.filterModel.filter;
      const response = await _getMonths(academicYear?.id).catch((error) => {
        this.ShowToast('Error', error.message, 'error');
      });
      if (response?.data) {
        this.filterModel.collections.months = response.data;
      }
    },
    async getProviders() {
      const shouldCallDeProviderOnly = true;
      const { academicYear, month } = this.filterModel.filter;
      const response = await _getProviders(
        academicYear?.id,
        month?.monthDesc,
        shouldCallDeProviderOnly
      ).catch((error) => {
        this.ShowToast('Error', error.message, 'error');
      });
      if (response?.data) {
        this.filterModel.collections.providers = response.data;
        if (this.filterModel.collections.providers.length === 1) {
          this.filterModel.filter.provider = this.filterModel.collections.providers[0];
          await this.getCenters();
        }
      }
    },
    async getCenters() {
      const { academicYear } = this.filterModel.filter;
      const responseData = await locationApi.getCenters({
        academicYearId: academicYear.id,
      });
      if (responseData) {
        this.filterModel.collections.centers = responseData;
        if (this.filterModel.collections.centers.length === 1) {
          this.filterModel.filter.center = this.filterModel.collections.centers[0];
          await this.getMunicipalities();
        }
      }
    },
    async getSpecialists() {
      const {
        academicYear,
        month,
        provider,
        location,
      } = this.filterModel.filter;
      const response = await _getSpecialists(
        academicYear?.id,
        month?.monthDesc,
        provider?.id,
        location?.id
      ).catch((error) => {
        this.ShowToast('Error', error.message, 'error');
      });
      if (response?.data) {
        this.filterModel.collections.specialists = response.data;
      }
    },
    async getMunicipalities() {
      const { academicYear, center } = this.filterModel.filter;
      const responseData = await locationApi.getMunicipalities({
        academicYearId: academicYear.id,
        centerId: center.id,
      });
      if (responseData) {
        this.filterModel.collections.municipalities = responseData;
        if (this.filterModel.collections.municipalities.length === 1) {
          this.filterModel.filter.municipality = this.filterModel.collections.municipalities[0];
          this.getLocationTypes();
          await this.getServices();
        }
      }
    },
    async getServices() {
      const { month, provider, specialist } = this.filterModel.filter;
      const response = await _getServices(
        month?.monthDesc,
        provider?.id,
        specialist?.id
      ).catch((error) => {
        this.ShowToast('Error', error.message, 'error');
      });
      if (response?.data) {
        this.filterModel.collections.services = response.data;
        if (this.filterModel.collections.services.length === 1) {
          this.filterModel.filter.service = this.filterModel.collections.services[0];
        }
      }
    },
    getLocationTypes() {
      if (this.filterModel.collections.locationTypes.length === 1) {
        this.filterModel.filter.locationType = this.filterModel.collections.locationTypes[0];
        this.getLocations();
      }
    },
    async getLocations() {
      const {
        academicYear,
        provider,
        specialist,
        locationType,
      } = this.filterModel.filter;
      if (
        !academicYear?.id &&
        !provider?.id &&
        !specialist?.id &&
        !locationType?.id
      ) {
        return;
      }
      const response = await _getLocations(
        academicYear?.id,
        provider?.id,
        specialist?.id,
        locationType?.id
      ).catch((error) => {
        this.ShowToast('Error', error.message, 'error');
      });
      if (response?.data) {
        this.filterModel.collections.locations = response.data;
        if (this.filterModel.collections.locations.length === 1) {
          this.filterModel.filter.location = this.filterModel.collections.locations[0];
        }
      }
    },
    clearModels(fields = []) {
      if (!this.isInitialized) return;
      const filterToCollectionMap = {
        academicYear: 'academicYears',
        month: 'months',
        provider: 'providers',
        specialist: 'specialists',
        center: 'centers',
        municipality: 'municipalities',
        service: 'services',
        locationType: 'locationTypes',
        location: 'locations',
        age: 'ages',
        action: 'actions',
      };
      fields.forEach((field) => {
        if (this.filterModel.filter.hasOwnProperty(field)) {
          this.filterModel.filter[field] = undefined;
        }
        const collectionName = filterToCollectionMap[field];
        if (
          collectionName &&
          this.filterModel.collections.hasOwnProperty(collectionName) &&
          collectionName !== 'locationTypes'
        ) {
          this.filterModel.collections[collectionName] = [];
        }
      });
    },
    async onSearch(pageIndex = 1) {
      var isValid = await this.$refs.filterRef.$refs.formValidator.validate();
      if (!isValid) {
        this.ShowToast(
          'Error',
          'Por favor, complete los campos requeridos.',
          'error'
        );
        return;
      }

      const filterValue = this.filterModel.filter;
      if (filterValue) {
        const payload = {
          academicYearId: filterValue.academicYear?.id,
          serviceDate: filterValue.month?.monthDesc,
          providerId: filterValue.provider?.id,
          specialistId: filterValue.specialist?.id,
          centerId: filterValue.center?.id,
          municipalityId: filterValue.municipality?.id,
          serviceId: filterValue.service?.id,
          locationTypeId: filterValue.locationType?.id,
          locationId: filterValue.location?.id,
          studentAge: filterValue.age?.id,
          actionId: filterValue.action?.id,
          studentSieId: filterValue.sie,
          pageIndex,
          pageSize: this.pagination.pageSize,
        };

        const response = await _searchVisits(
          payload,
          this.pagination.currentPage,
          this.pagination.pageSize
        ).catch((error) => {
          this.ShowToast('Error', error.message, 'error');
        });

        if (response?.data) {
          const data = response.data;
          this.results = data.data.map((x) => ({ ...x, isOpen: false }));

          this.pagination = {
            ...this.pagination,
            count: data.count,
            currentPage: data.currentPage,
            pageCount: data.pageCount,
            totalItemCount: data.totalItemCount,
          };
        }
      }
    },
    async handleSearch() {
      await this.onSearch();
    },
    async handlePageChange(page) {
      await this.onSearch(page);
    },
    handleToggle(item, itemIndex) {
      item.isOpen = !item.isOpen;
    },
    handleEnter(item) {
      const academicYearDescription = item.academicYearDescription;
      const selectedFilters = this.filterModel.filter;
      const months = this.filterModel.collections.months;
      const monthNumber = selectedFilters.month.monthNumber;

      const disabledPeriods = months
        .slice(_.findIndex(months, { id: selectedFilters.month.id }) + 1)
        .map(({ monthNumber, year }) =>
          moment()
            .date(1)
            .month(monthNumber - 1)
            .year(year)
            .toDate()
        );

      const initialPeriod = months[0];

      const currentPeriodYear = moment(item.academicYearStartDate).year();

      const initialPeriodInfo = {
        year: currentPeriodYear,
        monthNumber,
      };

      const currentPeriod = {
        year: parseInt(
          monthNumber > 6
            ? academicYearDescription.split('-')[0]
            : academicYearDescription.split('-')[1]
        ),
        monthNumber,
      };

      const params = {
        ...item,
        disabledPeriods,
        initialPeriod,
        initialPeriodInfo,
        currentPeriod,
      };

      this.$router.push({
        name: 'visit-registration',
        params: {
          ...params,
        },
      });
    },
    mergeFilter(newObj = {}, existingObj = {}) {
      const result = { ...newObj };
      Object.keys(existingObj).forEach((key) => {
        const existingValue = existingObj[key];
        if (existingValue !== null && existingValue !== undefined) {
          result[key] = existingValue;
        }
      });
      return result;
    },
    mergeArrayCollections(newCollections = {}, existingCollections = {}) {
      const result = { ...newCollections };
      Object.keys(existingCollections).forEach((key) => {
        if (
          Array.isArray(existingCollections[key]) &&
          Array.isArray(newCollections[key])
        ) {
          const newItems = newCollections[key] || [];
          const existingItems = existingCollections[key] || [];
          const localOnlyItems = existingItems.filter(
            (existingItem) =>
              !newItems.some(
                (newItem) =>
                  (newItem.id !== null && newItem.id === existingItem.id) ||
                  newItem.name === existingItem.name
              )
          );
          result[key] = [...newItems, ...localOnlyItems];
        }
      });
      return result;
    },
  },
};
</script>
<style scoped lang="scss"></style>
